import { type AtlasConfig, type Color } from '../model/AtlasConfig';

/**
 * Convertit une couleur hexadécimale (#RRGGBB) en RGB.
 * @param hex Couleur hexadécimale.
 * @returns Couleur en RGB.
 */
function hexToRgb(hex: string): Color {
  const bigint = parseInt(hex.slice(1), 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  };
}

/**
 * Génère une palette de 50 couleurs de l'arc-en-ciel.
 * @returns Tableau de couleurs en RGB.
 */
export function generateRainbowColors(numColors: number): Color[] {
  const colors: Color[] = [];
  for (let i = 0; i < numColors; i++) {
    const hue = (i * 360) / numColors;
    const colorHex = convertHslToHex(hue, 100, 50);
    colors.push(hexToRgb(colorHex));
  }
  return colors;
}

/**
 * Convertit une couleur HSL en hexadécimal (#RRGGBB).
 * @param h Teinte (hue), entre 0 et 360.
 * @param s Saturation (saturation), entre 0 et 100.
 * @param l Luminosité (lightness), entre 0 et 100.
 * @returns Couleur hexadécimale.
 */
function convertHslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0; let g = 0; let b = 0;
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
}

/**
 * Trouve la ligne et la colonne correspondant à la couleur la plus proche dans un atlas.
 * @param targetColor La couleur cible en RGB {r, g, b}.
 * @param atlasConfig La configuration de l'atlas avec les couleurs disponibles.
 * @returns Les coordonnées {row, col} de la cellule dans l'atlas.
 */
export function findClosestColorInAtlas(targetColor: Color, atlasConfig: AtlasConfig): { row: number; col: number } {
  const { cols, colors } = atlasConfig;

  let closestIndex = -1;
  let minDistance = Infinity;

  // Parcourir toutes les couleurs de l'atlas
  for (let i = 0; i < colors.length; i++) {
    const color = colors[i];
    const distance = Math.sqrt(
      Math.pow(color.r - targetColor.r, 2) +
      Math.pow(color.g - targetColor.g, 2) +
      Math.pow(color.b - targetColor.b, 2)
    );

    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = i;
    }
  }

  if (closestIndex === -1) {
    throw new Error("Aucune couleur trouvée dans l'atlas.");
  }

  // Calculer les coordonnées (row, col) à partir de l'index
  const row = Math.floor(closestIndex / cols);
  const col = closestIndex % cols;

  return { row, col };
}
